import React from "react";
import Typography from "../../../common/Typography";
import { MarkdownWrapper, ResponsiveImage } from "../../../common";
import { LearningOutcomesContent } from "../../../../types/newtypes";

interface OutcomesPreviewProps {
  content: LearningOutcomesContent;
  onCompleted?: () => void;
  // eslint-disable-next-line no-unused-vars
  onCardStateChange?: (isCompleted: boolean, canProceed: boolean) => void;
}

const OutcomesPreview: React.FC<OutcomesPreviewProps> = ({ content }) => {
  return (
    <div className="w-full">
      {/* Responsive Layout */}
      <div className="lg:grid lg:grid-cols-2 lg:gap-8">
        {/* Left Side - Content */}
        <div className="mb-8 lg:mb-0">
          {/* Main Title */}
          <Typography variant="h1" weight="bold" className="mb-6 text-gray-900">
            {content?.slide_title}
          </Typography>

          {/* Outcomes Sub-heading */}
          <MarkdownWrapper
            content={content?.intro_text || ""}
            className="!text-slate-600 !text-base leading-relaxed mb-8"
          />

          <Typography
            variant="h2"
            weight="medium"
            className="mb-4 text-gray-900"
          >
            {content?.title}
          </Typography>

          {/* Learning Outcomes List */}
          <div className="space-y-1">
            {content?.outcomes?.map((outcome, index) => (
              <div key={index} className="flex items-start gap-2">
                <Typography
                  variant="span"
                  weight="medium"
                  className="text-gray-900 flex-shrink-0 !text-sm"
                >
                  {index + 1}.
                </Typography>
                <Typography
                  variant="small"
                  className="text-gray-900 !text-sm leading-none"
                >
                  {(() => {
                    const [firstWord = "", ...rest] = outcome.split(" ");
                    return (
                      <>
                        <strong>{firstWord}</strong> {rest.join(" ")}
                      </>
                    );
                  })()}
                </Typography>
              </div>
            ))}
          </div>
        </div>

        {/* Right Side - Image (render only if provided) */}
        {content?.image_uri && (
          <div className="flex flex-col items-center justify-center w-full max-w-full overflow-hidden">
            <ResponsiveImage
              image_uri={content.image_uri}
              alt={content.alt_text || "Learning outcomes illustration"}
              // Constrain to container width to prevent overflow in grid column
              maxWidth="100%"
              maxHeight="500px"
              objectFit="contain"
              placeholderText="Learning outcomes illustration not available"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default OutcomesPreview;
